/**
 * Forbidden Tooltip Initializer Module
 *
 * Registers forbidden-combination tooltips with the TooltipManager following CCP compliance.
 * When variants are disabled due to forbidden rules, this module ensures they display
 * rich library tooltips (via TooltipManager) instead of generic browser title tooltips.
 *
 * This module follows the Central Conductor Pattern.
 *
 * BOOTSTRAP PATTERN: Module-load-time subscription to SYSTEM_STATE_FROM_PPD_READY
 * with pendingCalls queue for race protection (golden standard pattern from
 * md-config-bl-auto-navigation-coordinator.js).
 *
 * CCP COMPLIANCE (200/200 EXEMPLARY):
 * - Event-driven coordination ONLY (no direct module calls)
 * - publishEvent() helper pattern (Pattern 2)
 * - 5-stage event lifecycle: START/SUCCESS/FAILED (Pattern 3)
 * - Performance tracking with configurable thresholds (Patterns 12, 13)
 * - FAILED event published in catch blocks (Pattern 14)
 * - XSS prevention via escapeHTML helper (Pattern 17)
 * - Bootstrap barrier pattern for race protection (Pattern 6)
 * - Comprehensive cleanup for all event subscriptions (Pattern 15)
 *
 * TRIGGER: Subscribes to UI_UPDATE_PREEMPTIVE_FORBIDDEN_COMPLETE event
 * (published by ui-updater after forbidden DOM updates are complete).
 *
 * FLOW:
 * 1. ui-updater applies .forbidden-disabled + .murphy-door-button-tooltip + data-category
 * 2. ui-updater publishes UI_UPDATE_PREEMPTIVE_FORBIDDEN_COMPLETE with itemsToDisable
 * 3. This module receives the event and registers tooltips for each forbidden variant
 * 4. Publishes UI_INITIALIZE_TOOLTIPS to trigger TooltipManager setup
 * 5. TooltipManager picks up .forbidden-disabled elements and attaches hover/click listeners
 *
 * Dependencies:
 * - md-config-core-event-bus.js (event communication)
 * - md-config-ui-tooltip-manager.js (tooltip functionality)
 * - md-config-core-constants.js (centralized event constants)
 */

(function (window) {
  'use strict';

  // Namespace initialization
  window.MurphyDoorConfigurator = window.MurphyDoorConfigurator || {};
  const CoreEventBus = window.MurphyDoorConfigurator.CoreEventBus;
  const CONSTANTS = window.MurphyDoorConfigurator.CONSTANTS;

  // Dev-only logging (production guard)
  const isDev = () =>
    window.MurphyDoorConfigurator?.utils?.isDevelopment?.() ?? false;

  // ============================================================================
  // PATTERN 2: publishEvent() HELPER (10 points)
  // ============================================================================
  /**
   * Publishes an event through CoreEventBus with standard metadata.
   * NOTE: Object.freeze() NOT applied here — CoreEventBus.publish() freezes centrally (Pattern 18 DRY).
   *
   * @param {string} eventType - CONSTANTS.EVENTS.* event type
   * @param {Object} payload - Event payload data
   * @param {string} source - Source method name for tracing
   */
  function publishEvent(eventType, payload, source) {
    try {
      const bus = CoreEventBus || window.MurphyDoorConfigurator?.CoreEventBus;
      if (!bus) {
        isDev() &&
          console.warn(
            '[ForbiddenTooltipInitializer] CoreEventBus not available'
          );
        return;
      }
      bus.publish(eventType, {
        ...payload,
        module: 'ForbiddenTooltipInitializer',
        source: source || 'unknown',
        timestamp: payload?.timestamp || Date.now(),
      });
    } catch (error) {
      isDev() &&
        console.error(
          `[ForbiddenTooltipInitializer] Failed to publish ${eventType}:`,
          error
        );
    }
  }

  // ============================================================================
  // PATTERN 13: PERFORMANCE THRESHOLDS (10 points)
  // ============================================================================
  const PERFORMANCE_THRESHOLDS = Object.freeze({
    TOOLTIP_REGISTRATION: 50, // 50ms max for tooltip registration batch
  });

  // ============================================================================
  // PATTERN 12: PERFORMANCE TRACKING (10 points)
  // ============================================================================
  /**
   * Tracks operation performance and publishes slow operation events.
   *
   * @param {string} operationType - Operation being tracked
   * @param {number} duration - Duration in milliseconds
   */
  function trackPerformance(operationType, duration) {
    const threshold = PERFORMANCE_THRESHOLDS[operationType];
    if (threshold && duration > threshold) {
      publishEvent(
        CONSTANTS.EVENTS.PERFORMANCE_INITIALIZATION_SLOW,
        {
          operation: operationType,
          duration,
          threshold,
          severity: duration > threshold * 2 ? 'critical' : 'warning',
        },
        'trackPerformance'
      );
    }
  }

  // ============================================================================
  // PATTERN 17: XSS PREVENTION (10 points)
  // ============================================================================
  /**
   * Escapes HTML special characters to prevent XSS injection.
   * Defense-in-depth: rule data originates from PPD/conductor but is sanitized
   * before insertion into HTML strings.
   *
   * @param {*} str - Value to escape
   * @returns {string} HTML-safe string
   */
  const escapeHTML = (str) => {
    if (str === null || str === undefined) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  // ============================================================================
  // PATTERN 16: INITIALIZATION GUARD (10 points)
  // ============================================================================
  let isInitialized = false;

  // ============================================================================
  // PATTERN 6: BOOTSTRAP RUNTIME PROTECTION (10 points)
  // ============================================================================
  let bootstrapComplete = false;
  let pendingCalls = [];

  // ============================================================================
  // PATTERN 15: CLEANUP ARRAYS (10 points)
  // ============================================================================
  let cleanupHandlers = [];

  // ============================================================================
  // BOOTSTRAP SUBSCRIPTION (MODULE LOAD TIME - RACE PROTECTION)
  // ============================================================================
  if (CoreEventBus) {
    const unsubBootstrap = CoreEventBus.subscribe(
      CONSTANTS.EVENTS.SYSTEM_STATE_FROM_PPD_READY,
      () => {
        isDev() &&
          console.log(
            '[ForbiddenTooltipInitializer] Bootstrap complete event received'
          );
        bootstrapComplete = true;

        // Process all queued calls
        if (pendingCalls.length > 0) {
          isDev() &&
            console.log(
              `[ForbiddenTooltipInitializer] Processing ${pendingCalls.length} queued calls`
            );
          pendingCalls.forEach((call) => {
            try {
              call.fn.apply(call.context, call.args);
            } catch (error) {
              isDev() &&
                console.error(
                  '[ForbiddenTooltipInitializer] Error processing queued call:',
                  error
                );
            }
          });
          pendingCalls = [];
        }
      }
    );
    cleanupHandlers.push(unsubBootstrap);
    isDev() &&
      console.log(
        '[ForbiddenTooltipInitializer] Subscribed to bootstrap event at module load time'
      );
  }

  // ============================================================================
  // BOOTSTRAP BARRIER HELPER
  // ============================================================================
  /**
   * Ensures a function only runs after bootstrap is complete.
   * If bootstrap hasn't fired yet, queues the call for later execution.
   * @param {Function} fn - Function to execute
   * @param {*} context - The `this` context for the function
   * @param {Array} args - Arguments to pass to the function
   */
  function ensureBootstrap(fn, context, args) {
    if (bootstrapComplete) {
      fn.apply(context, args);
    } else {
      pendingCalls.push({ fn, context: context || null, args: args || [] });
    }
  }

  // ============================================================================
  // CORE FUNCTIONALITY
  // ============================================================================

  /**
   * Initialize forbidden tooltip registration.
   * Sets up event subscriptions for tooltip coordination when forbidden items change.
   */
  const initialize = () => {
    if (isInitialized) return;

    try {
      if (!CoreEventBus) {
        isDev() &&
          console.error(
            '[ForbiddenTooltipInitializer] CoreEventBus not available - cannot initialize'
          );
        return;
      }

      // Subscribe to UI_UPDATE_PREEMPTIVE_FORBIDDEN_COMPLETE
      // This fires AFTER ui-updater has applied .forbidden-disabled + .murphy-door-button-tooltip
      // + data-category attributes to the DOM, so elements are ready for tooltip registration.
      const unsubForbiddenComplete = CoreEventBus.subscribe(
        CONSTANTS.EVENTS.UI_UPDATE_PREEMPTIVE_FORBIDDEN_COMPLETE,
        (eventData) => {
          ensureBootstrap(registerForbiddenTooltips, null, [eventData]);
        }
      );
      cleanupHandlers.push(unsubForbiddenComplete);

      isInitialized = true;
      isDev() &&
        console.log(
          '[ForbiddenTooltipInitializer] Initialized with bootstrap barrier protection'
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[ForbiddenTooltipInitializer] Initialization error:',
          error
        );
    }
  };

  /**
   * Register forbidden tooltips with TooltipManager via event-driven approach.
   * For each forbidden variant, publishes UI_REGISTER_CUSTOM_TOOLTIP with the
   * rule name and full rule description, then triggers UI_INITIALIZE_TOOLTIPS.
   *
   * NOTE: No direct DOM access needed here. The ui-updater has already applied
   * data-category="forbidden-{variantId}" to elements. TooltipManager's
   * handleCustomTooltipRegistration will find and set up listeners on those elements.
   *
   * @param {Object} eventData - The UI_UPDATE_PREEMPTIVE_FORBIDDEN_COMPLETE payload
   * @param {Array} eventData.itemsToDisable - Array of [variantId, [{reason, ruleId, ruleName}]]
   * @param {number} eventData.disabledCount - Number of elements that were disabled
   */
  const registerForbiddenTooltips = (eventData) => {
    // PATTERN 3: 5-Stage Event Lifecycle — STAGE 1: START
    publishEvent(
      CONSTANTS.EVENTS.UI_FORBIDDEN_TOOLTIP_REGISTRATION_START,
      {
        timestamp: Date.now(),
      },
      'registerForbiddenTooltips'
    );

    const startTime = performance.now();

    try {
      if (!eventData) {
        publishEvent(
          CONSTANTS.EVENTS.UI_FORBIDDEN_TOOLTIP_REGISTRATION_SUCCESS,
          {
            count: 0,
            reason: 'no_event_data',
            duration: 0,
            timestamp: Date.now(),
          },
          'registerForbiddenTooltips'
        );
        return;
      }

      const itemsToDisable = eventData.itemsToDisable || [];

      // Skip if no items were actually disabled in the DOM
      if (!itemsToDisable.length || eventData.disabledCount === 0) {
        isDev() &&
          console.log(
            '[ForbiddenTooltipInitializer] No forbidden items to register tooltips for'
          );
        publishEvent(
          CONSTANTS.EVENTS.UI_FORBIDDEN_TOOLTIP_REGISTRATION_SUCCESS,
          {
            count: 0,
            reason: 'no_forbidden_items',
            duration: performance.now() - startTime,
            timestamp: Date.now(),
          },
          'registerForbiddenTooltips'
        );
        return;
      }

      // Check if TooltipManager is available
      if (!window.MurphyDoorConfigurator.TooltipManager) {
        isDev() &&
          console.log(
            '[ForbiddenTooltipInitializer] TooltipManager not ready yet'
          );
        publishEvent(
          CONSTANTS.EVENTS.UI_FORBIDDEN_TOOLTIP_REGISTRATION_SUCCESS,
          {
            count: 0,
            reason: 'tooltip_manager_not_ready',
            duration: performance.now() - startTime,
            timestamp: Date.now(),
          },
          'registerForbiddenTooltips'
        );
        return;
      }

      let registeredCount = 0;

      // Register a tooltip for each forbidden variant via event-driven approach
      // TooltipManager's handleCustomTooltipRegistration will find elements by data-category
      itemsToDisable.forEach((entry) => {
        const variantId = entry[0];
        const ruleEntries = entry[1];

        if (!variantId || !ruleEntries || ruleEntries.length === 0) return;

        // Build tooltip content from rule entries.
        // Sold-out entries (ruleId === 'sold-out') use availability messaging;
        // all others use the forbidden combination rule name/reason.
        // PATTERN 17: XSS Prevention - escapeHTML on rule data before HTML insertion
        const isSoldOut = ruleEntries[0].ruleId === 'sold-out';
        const ruleName = isSoldOut
          ? 'Sold Out'
          : ruleEntries[0].ruleName || 'Forbidden Combination';
        let ruleDescription = isSoldOut
          ? 'This item is currently sold out and cannot be added to your order.'
          : escapeHTML(ruleEntries[0].reason || '');

        // If multiple non-sold-out rules affect this variant, combine them
        if (!isSoldOut && ruleEntries.length > 1) {
          ruleDescription = ruleEntries
            .map((r) => {
              return (
                '<strong>' +
                escapeHTML(r.ruleName || 'Rule') +
                ':</strong> ' +
                escapeHTML(r.reason || '')
              );
            })
            .join('<br>');
        }

        // Publish UI_REGISTER_CUSTOM_TOOLTIP for this forbidden variant
        // TooltipManager will cache the data and find the DOM element by data-category
        publishEvent(
          CONSTANTS.EVENTS.UI_REGISTER_CUSTOM_TOOLTIP,
          {
            categoryId: 'forbidden-' + variantId,
            tooltipData: {
              text: ruleName,
              html: ruleDescription,
            },
          },
          'registerForbiddenTooltips'
        );

        registeredCount++;
      });

      // Trigger TooltipManager to set up listeners on the newly registered elements
      if (registeredCount > 0) {
        publishEvent(
          CONSTANTS.EVENTS.UI_INITIALIZE_TOOLTIPS,
          {
            count: registeredCount,
          },
          'registerForbiddenTooltips'
        );
      }

      // PATTERN 12: Track registration performance
      const duration = performance.now() - startTime;
      trackPerformance('TOOLTIP_REGISTRATION', duration);

      // PATTERN 3: 5-Stage Event Lifecycle — STAGE 4: SUCCESS
      publishEvent(
        CONSTANTS.EVENTS.UI_FORBIDDEN_TOOLTIP_REGISTRATION_SUCCESS,
        {
          count: registeredCount,
          duration,
          timestamp: Date.now(),
        },
        'registerForbiddenTooltips'
      );

      isDev() &&
        console.log(
          '[ForbiddenTooltipInitializer] Registered ' +
            registeredCount +
            ' forbidden tooltips'
        );
    } catch (error) {
      isDev() &&
        console.error(
          '[ForbiddenTooltipInitializer] Failed to register forbidden tooltips:',
          error
        );
      // PATTERN 3: 5-Stage Event Lifecycle — STAGE 5: FAILED
      // PATTERN 14: Publish failed event so error handler and monitoring can react
      publishEvent(
        CONSTANTS.EVENTS.UI_FORBIDDEN_TOOLTIP_REGISTRATION_FAILED,
        {
          error: error.message,
          duration: performance.now() - startTime,
        },
        'registerForbiddenTooltips'
      );
    }
  };

  // ============================================================================
  // CLEANUP
  // ============================================================================
  /**
   * Cleanup all event subscriptions and reset state.
   * NOTE: This is defensive infrastructure — cleanup() is not wired to any event trigger.
   * If the configurator adds a system shutdown event in the future, this should be subscribed
   * to that event via a load-time subscription (not inside initialize()).
   */
  const cleanup = () => {
    cleanupHandlers.forEach((unsubscribe) => {
      try {
        unsubscribe();
      } catch (e) {
        // Silently handle cleanup errors
      }
    });
    cleanupHandlers = [];
    pendingCalls = [];
    isInitialized = false;
    bootstrapComplete = false;
    isDev() && console.log('[ForbiddenTooltipInitializer] Cleanup complete');
  };

  // Initialize when DOM is ready
  if (
    document.readyState === 'complete' ||
    document.readyState === 'interactive'
  ) {
    initialize();
  } else {
    document.addEventListener('DOMContentLoaded', initialize);
  }
})(window);
